import {
  NumberOrNullOrUndefined,
  StringOrNullOrUndefined,
  BooleanOrNullOrUndefined,
} from 'src/app/shared/utils';

export interface InsuranceServiceInterface {
  id: number;
  insurance_id: number;
  location_id: number;
  cpt_code: string;
  modifiers?: string;
  provider_types: string;
  description: string;
  unit_price: string; // Decimal as string (e.g., "65.00")
  max_per_day: number;
  auto_on_pos?: string;
  auto_on_provider?: string;
  auto_on_same_day: boolean;
  auto_on_overlapping?: string;
  
  // Computed attributes
  provider_types_array: string[];
  modifiers_array: string[];
  
  // Relationships (when included)
  insurance?: {
    id: number;
    name: string;
  };
  location?: {
    id: number;
    title: string;
  };
  
  // Timestamps
  created_at: string | Date;
  updated_at: string | Date;
}

export class InsuranceServiceV2 {
  id: number = 0;
  insurance_id: number = 0;
  location_id: number = 0;
  cpt_code: string = '';
  modifiers?: string;
  provider_types: string = '';
  description: string = '';
  unit_price: string = '';
  max_per_day: number = 0;
  auto_on_pos?: string;
  auto_on_provider?: string;
  auto_on_same_day: boolean = false;
  auto_on_overlapping?: string;
  
  // Computed attributes
  provider_types_array: string[] = [];
  modifiers_array: string[] = [];
  
  // Relationships
  insurance?: {
    id: number;
    name: string;
  };
  location?: {
    id: number;
    title: string;
  };
  
  // Timestamps
  created_at: string | Date = '';
  updated_at: string | Date = '';

  constructor(data: Partial<InsuranceServiceInterface> | null | undefined) {
    if (!data) return;

    this.id = NumberOrNullOrUndefined(data.id) ?? 0;
    this.insurance_id = NumberOrNullOrUndefined(data.insurance_id) ?? 0;
    this.location_id = NumberOrNullOrUndefined(data.location_id) ?? 0;
    this.cpt_code = StringOrNullOrUndefined(data.cpt_code) ?? '';
    this.modifiers = StringOrNullOrUndefined(data.modifiers);
    this.provider_types = StringOrNullOrUndefined(data.provider_types) ?? '';
    this.description = StringOrNullOrUndefined(data.description) ?? '';
    this.unit_price = StringOrNullOrUndefined(data.unit_price) ?? '';
    this.max_per_day = NumberOrNullOrUndefined(data.max_per_day) ?? 0;
    this.auto_on_pos = StringOrNullOrUndefined(data.auto_on_pos);
    this.auto_on_provider = StringOrNullOrUndefined(data.auto_on_provider);
    this.auto_on_same_day = BooleanOrNullOrUndefined(data.auto_on_same_day) ?? false;
    this.auto_on_overlapping = StringOrNullOrUndefined(data.auto_on_overlapping);

    // Computed attributes
    this.provider_types_array = this.parseProviderTypes(data.provider_types);
    this.modifiers_array = this.parseModifiers(data.modifiers);

    // Relationships
    this.insurance = data.insurance;
    this.location = data.location;

    // Timestamps
    this.created_at = data.created_at ? new Date(data.created_at) : '';
    this.updated_at = data.updated_at ? new Date(data.updated_at) : '';
  }

  private parseProviderTypes(providerTypes?: string): string[] {
    if (!providerTypes) return [];
    return providerTypes.split(',').map(type => type.trim());
  }

  private parseModifiers(modifiers?: string): string[] {
    if (!modifiers) return [];
    return modifiers.split('-').map(modifier => modifier.trim());
  }

  static build(data: unknown): InsuranceServiceV2 {
    return new InsuranceServiceV2(data as Partial<InsuranceServiceInterface>);
  }

  // Helper method to get formatted unit price as number
  get unitPriceAsNumber(): number {
    return parseFloat(this.unit_price) || 0;
  }

  // Helper method to get provider types as comma-separated string
  get providerTypesString(): string {
    return this.provider_types_array.join(', ');
  }

  // Helper method to get modifiers as dash-separated string
  get modifiersString(): string {
    return this.modifiers_array.join('-');
  }
}
